export function PricePerSquareFoot() {
  return (
    <section className="py-20 px-4 bg-gray-50">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl lg:text-5xl font-bold text-gray-900 mb-6">
            Quick Valuation Method: Price Per Square Foot
          </h2>
          <p className="text-xl text-gray-600 max-w-4xl mx-auto leading-relaxed">
            Price per square foot is another quick metric for estimating apartment building value. While more commonly used for single-family homes, it can provide useful insights when comparing similar multifamily properties.
          </p>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">What Is Price Per Square Foot?</h3>
            <p className="text-lg text-gray-700 mb-4 leading-relaxed">
              Price per square foot divides the total sale price by the building's total rentable square footage. This metric helps normalize property values across different sizes and provides a quick comparison tool for similar properties.
            </p>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              The formula is: <strong>Price Per SF = Total Sale Price ÷ Total Square Feet</strong>. For example, if a 20,000 square foot apartment building sells for $2,000,000, the price per square foot is $100. You can then apply this to your property's square footage for a quick estimate.
            </p>
            <div className="bg-teal-50 border-l-4 border-teal-600 p-6 rounded-r-lg">
              <h4 className="font-bold text-gray-900 mb-2 text-lg">Simple Formula:</h4>
              <p className="text-gray-800 text-xl font-semibold mb-2">Price Per SF = Sale Price ÷ Square Feet</p>
              <p className="text-gray-600">Useful for comparing properties of different sizes</p>
            </div>
          </div>
          <div>
            <img
              src="https://public.readdy.ai/ai/img_res/4e7a8da344ce93c208d7d2b37ef67fc4.jpg"
              alt="Price per square foot calculation for apartment buildings"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">How to Calculate and Use Price Per Square Foot</h3>
          
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">1</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Determine Total Square Footage</h4>
              <p className="text-gray-600 leading-relaxed">
                Calculate or obtain the total rentable square footage of your apartment building from property records or measurements.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">2</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Research Comparable Sales</h4>
              <p className="text-gray-600 leading-relaxed">
                Find recent sales of similar properties and calculate their price per square foot to establish market benchmarks.
              </p>
            </div>

            <div className="text-center">
              <div className="w-16 h-16 flex items-center justify-center bg-teal-100 rounded-full mx-auto mb-4">
                <span className="text-teal-600 font-bold text-2xl">3</span>
              </div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Apply to Your Property</h4>
              <p className="text-gray-600 leading-relaxed">
                Multiply the average market price per square foot by your building's square footage for a value estimate.
              </p>
            </div>
          </div>

          <div className="max-w-4xl mx-auto bg-gradient-to-r from-teal-600 to-teal-700 rounded-lg p-8 text-white">
            <h4 className="text-2xl font-bold mb-6 text-center">Price Per Square Foot Example</h4>
            
            <div className="space-y-4 mb-6">
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Comp #1: 18,000 SF sold for $1,890,000</span>
                  <span className="font-bold">$105/SF</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Comp #2: 22,000 SF sold for $2,310,000</span>
                  <span className="font-bold">$105/SF</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Comp #3: 20,000 SF sold for $2,000,000</span>
                  <span className="font-bold">$100/SF</span>
                </div>
              </div>
              <div className="bg-white/10 rounded-lg p-4">
                <div className="flex justify-between items-center">
                  <span>Comp #4: 24,000 SF sold for $2,520,000</span>
                  <span className="font-bold">$105/SF</span>
                </div>
              </div>
            </div>

            <div className="border-t border-white/30 pt-6">
              <p className="text-lg mb-2">Average Price Per Square Foot: <strong>$103.75/SF</strong></p>
              <p className="text-lg mb-4">Your Property: <strong>20,000 square feet</strong></p>
              <p className="text-2xl font-bold">Estimated Value: 20,000 × $103.75 = <strong>$2,075,000</strong></p>
            </div>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-12 items-center mb-16">
          <div>
            <img
              src="https://readdy.ai/api/search-image?query=apartment-unit-sizes-different-floor-plans-studio-one-bedroom-two-bedroom-three-bedroom-layouts-unit-mix-variety-apartment-configurations-space-planning-residential-layouts-architectural-plans&width=800&height=600&seq=unit-sizes-comparison-001&orientation=landscape"
              alt="Different apartment unit sizes and configurations"
              className="w-full h-96 object-cover object-top rounded-lg shadow-lg"
            />
          </div>
          <div>
            <h3 className="text-3xl font-bold text-gray-900 mb-6">Factors Affecting Price Per Square Foot</h3>
            <p className="text-lg text-gray-700 mb-6 leading-relaxed">
              Price per square foot varies significantly based on unit mix, efficiency ratio, location, and property quality. Understanding these factors helps you make appropriate adjustments.
            </p>
            <div className="space-y-4">
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-layout-grid-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Unit Mix & Size</h4>
                  <p className="text-gray-600">Smaller units (studios/1BR) typically have higher price per SF than larger units (2-3BR)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-pie-chart-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Efficiency Ratio</h4>
                  <p className="text-gray-600">Buildings with more common area (hallways, lobbies) have lower efficiency and may show lower price per SF</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-map-pin-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Location Premium</h4>
                  <p className="text-gray-600">Urban core properties command $150-$300/SF while suburban properties may be $75-$150/SF</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-star-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Finish Quality</h4>
                  <p className="text-gray-600">Luxury finishes and high-end amenities significantly increase price per square foot</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <div className="w-8 h-8 flex items-center justify-center bg-teal-100 rounded-full flex-shrink-0 mt-1">
                  <i className="ri-calendar-line text-teal-600 text-lg"></i>
                </div>
                <div>
                  <h4 className="font-semibold text-gray-900 mb-1">Building Age</h4>
                  <p className="text-gray-600">Newer construction typically commands 20-40% higher price per SF than older buildings</p>
                </div>
              </div>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-lg p-8 lg:p-12 mb-12">
          <h3 className="text-3xl font-bold text-gray-900 mb-8 text-center">Price Per Square Foot by Market Type</h3>
          
          <div className="grid md:grid-cols-3 gap-6">
            <div className="bg-gradient-to-br from-teal-50 to-teal-100 rounded-lg p-6 border-2 border-teal-600">
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">Major Metro Markets</h4>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class A Properties</p>
                  <p className="text-2xl font-bold text-teal-600">$200-$400/SF</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class B Properties</p>
                  <p className="text-2xl font-bold text-teal-600">$125-$200/SF</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class C Properties</p>
                  <p className="text-2xl font-bold text-teal-600">$75-$125/SF</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-yellow-50 to-yellow-100 rounded-lg p-6 border-2 border-yellow-600">
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">Secondary Markets</h4>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class A Properties</p>
                  <p className="text-2xl font-bold text-yellow-600">$125-$200/SF</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class B Properties</p>
                  <p className="text-2xl font-bold text-yellow-600">$85-$125/SF</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class C Properties</p>
                  <p className="text-2xl font-bold text-yellow-600">$50-$85/SF</p>
                </div>
              </div>
            </div>

            <div className="bg-gradient-to-br from-orange-50 to-orange-100 rounded-lg p-6 border-2 border-orange-600">
              <h4 className="text-xl font-bold text-gray-900 mb-4 text-center">Tertiary Markets</h4>
              <div className="space-y-3">
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class A Properties</p>
                  <p className="text-2xl font-bold text-orange-600">$85-$125/SF</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class B Properties</p>
                  <p className="text-2xl font-bold text-orange-600">$60-$85/SF</p>
                </div>
                <div className="bg-white rounded-lg p-4">
                  <p className="font-semibold text-gray-900 mb-1">Class C Properties</p>
                  <p className="text-2xl font-bold text-orange-600">$35-$60/SF</p>
                </div>
              </div>
            </div>
          </div>

          <p className="text-center text-gray-600 mt-8 text-lg">
            These ranges are general guidelines and can vary significantly based on specific location, property condition, and market conditions.
          </p>
        </div>

        <div className="bg-yellow-50 border-l-4 border-yellow-500 p-8 rounded-r-lg">
          <div className="flex items-start gap-4">
            <div className="w-12 h-12 flex items-center justify-center bg-yellow-100 rounded-full flex-shrink-0">
              <i className="ri-alert-line text-yellow-600 text-2xl"></i>
            </div>
            <div>
              <h4 className="text-xl font-bold text-gray-900 mb-3">Limitations of Price Per Square Foot</h4>
              <p className="text-lg text-gray-700 mb-4 leading-relaxed">
                Price per square foot is less reliable for apartment buildings than for single-family homes because it doesn't account for income production, which is the primary driver of multifamily value. Two buildings with identical square footage can have vastly different values based on rent levels and operating expenses.
              </p>
              <p className="text-lg text-gray-700 leading-relaxed">
                <strong>Best Practice:</strong> Use price per square foot as a supplementary metric alongside price per unit and cap rate analysis. For accurate valuation, always prioritize the income approach, which directly reflects the property's ability to generate returns for investors.
              </p>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}